import type { BrandSpecial } from '@iheartjane/data-access';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import type {
  PriceId,
  ProductWeight,
  QualifyingStatusEnum,
} from '@iheartjane/types';

import type { AlgoliaProduct } from '@jane/search/types';
// eslint-disable-next-line no-restricted-imports
import type { PendingCartProduct } from '@jane/shared/models';
import type { MenuProduct } from '@jane/shared/types';
import { isSoldByWeight, maybeParseFloat } from '@jane/shared/util';

export const getFilterForSpecialType = (
  specialType?: string,
  qualifyingStatus?: QualifyingStatusEnum
) => {
  switch (specialType) {
    case 'product': {
      return 'brand_special_ids';
    }
    case 'buy_x_and_save':
    case 'buy_x_get_y': {
      return qualifyingStatus === 'applied' || qualifyingStatus === 'qualified'
        ? 'bundle_brand_special_ids.discount'
        : 'bundle_brand_special_ids.qualify';
    }
    default: {
      return 'brand_special_ids';
    }
  }
};

const productInCart = (
  cartProducts: PendingCartProduct[],
  productId: number,
  priceId?: PriceId
) => {
  return cartProducts?.some(
    (cartProduct) =>
      cartProduct.product_id === productId &&
      (!priceId || cartProduct.price_id === priceId)
  );
};

interface ShouldShowWeight {
  cartProducts?: PendingCartProduct[];
  currentSpecial?: BrandSpecial;
  qualifyingStatus?: QualifyingStatusEnum;
}

/**
 * Creates a weight filtering function based on brand special conditions.
 * Returns a function that determines whether a specific weight should be shown,
 * handling cases like cart products, qualifying status, and brand special rules.
 * The returned function maintains closure over the provided special conditions, cart state,
 * and qualifying status, allowing efficient reuse across multiple weight checks.
 *
 * Handles different brand special types:
 * - Product: Uses applicable_weights array directly
 * - Bundle (buy_x_and_save/buy_x_get_y): Uses applicable_weights.qualify when not qualified,
 *   applicable_weights.discount when qualified
 */
export const shouldShowWeight = ({
  cartProducts = [],
  currentSpecial,
  qualifyingStatus,
}: ShouldShowWeight) => {
  return (weight: string, product: MenuProduct | AlgoliaProduct) => {
    if (!currentSpecial) return true;

    const productId = 'id' in product ? product.id : product.product_id;
    const weightId = weight.replace(' ', '_') as ProductWeight;

    const stringPrice = product[`price_${weightId}`];
    const price = stringPrice && maybeParseFloat(stringPrice);

    // If the weight is included in the special but this product just doesn't have that weight.
    if (!price) return false;

    // If the product at this weight is in the cart, keep it.
    if (productInCart(cartProducts, productId, weightId)) return true;

    // For product type, just check if weight is in applicable_weights
    if (currentSpecial?.special_type === 'product') {
      if (
        Array.isArray(currentSpecial.applicable_weights) &&
        currentSpecial.applicable_weights.length > 0 &&
        !currentSpecial.applicable_weights.includes(weightId)
      ) {
        return false;
      }
      return true;
    }

    // Handle bundle conditions - use applicable_weights object
    if (
      currentSpecial?.special_type === 'buy_x_and_save' ||
      currentSpecial?.special_type === 'buy_x_get_y'
    ) {
      // Check if applicable_weights is an object (bundle format)
      if (
        currentSpecial.applicable_weights &&
        typeof currentSpecial.applicable_weights === 'object' &&
        !Array.isArray(currentSpecial.applicable_weights)
      ) {
        const isQualified = ['qualified', 'applied'].includes(
          qualifyingStatus || ''
        );

        const relevantWeights = isQualified
          ? currentSpecial.applicable_weights.discount
          : currentSpecial.applicable_weights.qualify;

        if (relevantWeights?.length && !relevantWeights.includes(weightId)) {
          return false;
        }
      }

      return true;
    }

    return true;
  };
};

/**
 * Filters out products that would have no valid weights after applying brand special conditions.
 * Products in cart are always kept regardless of weight restrictions.
 */
export const filterProductsByWeight = ({
  cartProducts = [],
  currentSpecial,
  products,
  qualifyingStatus,
}: ShouldShowWeight & {
  products: SmartSortProduct<AlgoliaProduct>[];
}) => {
  if (!currentSpecial) return products;

  const weightFilter = shouldShowWeight({
    currentSpecial,
    cartProducts,
    qualifyingStatus,
  });

  return products.filter((product) => {
    const productKind = product.attributes['kind'];

    if (!isSoldByWeight(productKind)) return true;

    const productId = product.attributes['product_id'];

    const availableWeights = product.attributes['available_weights'];

    if (productInCart(cartProducts, productId)) return true;

    return (availableWeights || []).some((weight: string) => {
      const filter = weightFilter(weight, product.attributes);
      return filter;
    });
  });
};
